import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react'
import styles from './Dialog.module.css'

type Props = {
	open: boolean
	onClose: () => void
	children: ReactNode
}

// Native <dialog> shell: owns the show/close mechanics (top-layer, focus
// trap, ESC-to-close for free) and backdrop-click-to-dismiss. Each caller
// supplies its own .panel as the child — a <div> or a <form>. Centred (UA
// default inset:0 + margin:auto): a bottom-anchored panel puts its field
// where the iOS keyboard opens, shifting the whole viewport up; centred
// lands it mid-screen, above the keyboard, so it stays put.
export const Dialog = ({ open, onClose, children }: Props) => {
	const dialogRef = useRef<HTMLDialogElement>(null)

	useEffect(() => {
		const dialog = dialogRef.current
		if (!dialog) return
		if (open && !dialog.open) {
			dialog.showModal()
			// showModal() autofocuses the first focusable child, which then
			// shows a :focus-visible ring just from opening — and iOS Safari
			// draws that ring square, making the first pill look square until
			// the next render. Absorb the autofocus onto the dialog itself;
			// children that want a specific field focused do so in their own
			// effect, which runs after this one.
			dialog.focus()
		}
		if (!open && dialog.open) dialog.close()
	}, [open])

	useEffect(() => {
		const dialog = dialogRef.current
		if (!dialog) return
		const handler = () => onClose()
		dialog.addEventListener('close', handler)
		return () => dialog.removeEventListener('close', handler)
	}, [onClose])

	const onBackdrop = (e: MouseEvent<HTMLDialogElement>) => {
		if (e.target === e.currentTarget) onClose()
	}

	return (
		<dialog
			ref={dialogRef}
			className={styles.dialog}
			tabIndex={-1}
			onClick={onBackdrop}
		>
			{children}
		</dialog>
	)
}
